import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Instagram, Check, Loader2, Send } from "lucide-react";
import axios from "axios";
import { SectionHead, Reveal, Magnetic } from "./effects";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#D4AF37]/70 focus:bg-[#D4AF37]/5 focus:shadow-[0_0_24px_rgba(212,175,55,0.12)] transition-all duration-300";

const projectTypes = ["Short-Form Editing", "YouTube / Long-Form", "Commercial / Brand", "Motion Graphics", "Full Content Package", "Other"];
const budgets = ["On Inquiry", "$500 – $1,000", "$1,000 – $3,000", "$3,000 – $10,000", "$10,000+"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", project_type: "", budget: "", message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[400px] w-[800px] rounded-full bg-[#D4AF37]/10 blur-[160px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-20 items-start">
          <div>
            <SectionHead
              testId="contact"
              eyebrow="Contact"
              title={
                <>
                  Let's Build <br />
                  <span className="text-gradient-blue">Something Great</span>
                </>
              }
              sub="Ready to level up your content? Tell me about your project — I reply to every serious inquiry within 24 hours."
            />
            <Reveal delay={0.2}>
              <div className="space-y-4">
                <div className="glass rounded-2xl p-5 flex items-center gap-4" data-testid="contact-info-name">
                  <img src="/logo.png" alt="Sheen Creates logo" className="h-11 w-11 rounded-xl border border-[#D4AF37]/30 object-cover" />
                  <div>
                    <div className="text-sm font-semibold">Nausheen Fatima</div>
                    <div className="text-xs text-slate-500">Freelance Video Editor — Sheen Creates</div>
                  </div>
                </div>
                <a
                  href="mailto:noorainfatima077@gmail.com"
                  data-testid="contact-email-link"
                  className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-[#D4AF37]/50 transition-colors duration-300 group"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#F5DE81] group-hover:scale-110 transition-transform duration-300">
                    <Mail size={18} />
                  </span>
                  <div>
                    <div className="text-sm font-semibold">noorainfatima077@gmail.com</div>
                    <div className="text-xs text-slate-500">Email me directly — budget on inquiry, custom quotes</div>
                  </div>
                </a>
                <a
                  href="https://instagram.com/sheen.creates"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-[#D4AF37]/50 transition-colors duration-300 group"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#F5DE81] group-hover:scale-110 transition-transform duration-300">
                    <Instagram size={18} />
                  </span>
                  <div>
                    <div className="text-sm font-semibold">@sheen.creates</div>
                    <div className="text-xs text-slate-500">DM me on Instagram — fastest reply</div>
                  </div>
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="relative glass-accent rounded-3xl p-7 sm:p-10 overflow-hidden">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    data-testid="contact-success-message"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="py-16 flex flex-col items-center text-center"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.15 }}
                      className="relative mb-8"
                    >
                      <span className="absolute inset-0 rounded-full bg-[#D4AF37]/30 blur-2xl animate-glow-pulse" />
                      <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-[#D4AF37] shadow-[0_0_60px_rgba(212,175,55,0.6)]">
                        <Check size={36} className="text-white" strokeWidth={3} />
                      </span>
                    </motion.div>
                    <h3 className="font-display text-2xl sm:text-3xl font-semibold mb-3">Message Sent</h3>
                    <p className="text-sm text-slate-400 max-w-sm leading-relaxed mb-8">
                      Thanks, {form.name.split(" ")[0] || "there"} — your project brief is in. Expect a reply within 24 hours.
                    </p>
                    <button
                      data-testid="contact-send-another-btn"
                      onClick={() => {
                        setStatus("idle");
                        setForm({ name: "", email: "", company: "", project_type: "", budget: "", message: "" });
                      }}
                      className="text-xs uppercase tracking-[0.2em] text-[#F5DE81] hover:text-white transition-colors"
                    >
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="contact-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -16 }}
                    onSubmit={submit}
                    className="space-y-5"
                  >
                    <div className="grid sm:grid-cols-2 gap-5">
                      <input data-testid="contact-name-input" required placeholder="Your Name" value={form.name} onChange={set("name")} className={inputCls} />
                      <input data-testid="contact-email-input" required type="email" placeholder="Email Address" value={form.email} onChange={set("email")} className={inputCls} />
                    </div>
                    <input data-testid="contact-company-input" placeholder="Company / Channel (optional)" value={form.company} onChange={set("company")} className={inputCls} />
                    <div className="grid sm:grid-cols-2 gap-5">
                      <select data-testid="contact-project-type-select" required value={form.project_type} onChange={set("project_type")} className={`${inputCls} appearance-none ${!form.project_type ? "text-slate-600" : ""}`}>
                        <option value="" disabled className="bg-[#0a0a0c]">Project Type</option>
                        {projectTypes.map((t) => (
                          <option key={t} value={t} className="bg-[#0a0a0c]">{t}</option>
                        ))}
                      </select>
                      <select data-testid="contact-budget-select" value={form.budget} onChange={set("budget")} className={`${inputCls} appearance-none ${!form.budget ? "text-slate-600" : ""}`}>
                        <option value="" disabled className="bg-[#0a0a0c]">Budget (on inquiry)</option>
                        {budgets.map((b) => (
                          <option key={b} value={b} className="bg-[#0a0a0c]">{b}</option>
                        ))}
                      </select>
                    </div>
                    <textarea
                      data-testid="contact-message-input"
                      required
                      rows={4}
                      placeholder="Tell me about your project — goals, timeline, references..."
                      value={form.message}
                      onChange={set("message")}
                      className={`${inputCls} resize-none`}
                    />
                    {status === "error" && (
                      <p data-testid="contact-error-message" className="text-xs text-red-400">
                        Something went wrong — please email me directly at noorainfatima077@gmail.com
                      </p>
                    )}
                    <Magnetic strength={0.2} className="w-full">
                      <button
                        data-testid="contact-submit-btn"
                        type="submit"
                        disabled={status === "sending"}
                        className="w-full inline-flex items-center justify-center gap-3 rounded-xl bg-[#D4AF37] hover:bg-[#C9A227] disabled:opacity-60 text-white text-sm font-semibold px-7 py-4 transition-all duration-300 shadow-[0_0_40px_rgba(212,175,55,0.35)] hover:shadow-[0_0_60px_rgba(212,175,55,0.55)]"
                      >
                        {status === "sending" ? (
                          <>
                            <Loader2 size={16} className="animate-spin" /> Sending...
                          </>
                        ) : (
                          <>
                            Start Your Project <Send size={15} />
                          </>
                        )}
                      </button>
                    </Magnetic>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
